import { CommonModule, DatePipe } from "@angular/common";
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  input,
  OnInit,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AlgoMode, BudgetFlight, Currency } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { AlgoModeStr } from "@front/m19-models";
import { AccountSelectionService, AuthService, CurrencyService, TargetConfig } from "@front/m19-services";
import {
  IButtonDirective,
  IDatepickerComponent,
  IDateRange,
  IErrorComponent,
  IFormFieldComponent,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupSuffixDirective,
  ILabelComponent,
  IRadioComponent,
  IRadioGroupComponent,
  ITooltipDirective,
} from "@front/m19-ui";
import { API_DATE_FORMAT, DateUtils } from "@front/m19-utils";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { SpendProgressBarComponent } from "@m19-board/shared/spend-progress-bar/spend-progress-bar.component";
import { algorithmValidator, dailyBudgetLowerBound } from "@m19-board/utils/validators/algoValidators";
import { DateTime } from "luxon";
import { distinctUntilChanged, map, switchMap, take } from "rxjs";
import { BudgetCurrencyBadgeComponent } from "../budget-currency-badge/budget-currency-badge.component";
import { MonthlyBudgetWarningComponent } from "../monthly-budget-warning/monthly-budget-warning.component";
import { SchemaPath, validate } from "@angular/forms/signals";

export interface AlgoModeForm {
  algoMode: FormControl<AlgoMode | undefined>;
  acosTarget: FormControl<number | undefined>;
  dailyBudget: FormControl<number | undefined>;
  monthlyBudget: FormControl<number | undefined>;
  suggestedBid: FormControl<number | undefined>;
  nextMonthlyBudget: FormControl<number | undefined>;
  tacosTarget: FormControl<number | undefined>;
  budget: FormControl<number | undefined>;
  budgetDates: FormControl<IDateRange | undefined>;
}

interface AlgoModeStr {
  title: string;
  description: string;
}

type AlgoModeStrKey = {
  [key in AlgoMode]: AlgoModeStr;
};

export type AlgoModeConfig = {
  algoMode: AlgoMode;
  acosTarget: number;
  dailyBudget: number;
  monthlyBudget: number;
  suggestedBid: number;
  tacosTarget?: number;
  nextMonthlyBudget?: number;
  budgetFlight?: BudgetFlight;
  isValid?: boolean;
};

export const AlgoModeStrKeys = {
  [AlgoMode.PRODUCT_LAUNCH]: {
    title: "algo-mode-selection.constant_bid",
    description: "algo-mode-selection.force_product_visibility",
  },
  [AlgoMode.ACOS_TARGET]: {
    title: "algo-mode-selection.acos_target",
    description: "algo-mode-selection.optimize_sales_with_acos_target",
  },
  [AlgoMode.MONTHLY_BUDGET_TARGET]: {
    title: "algo-mode-selection.monthly_budget_target",
    description: "algo-mode-selection.optimize_sales_with_monthly_budget_target",
  },
  [AlgoMode.TACOS_TARGET]: {
    title: "algo-mode-selection.tacos_target",
    description: "algo-mode-selection.optimize_sales_with_tacos_target",
  },
  [AlgoMode.AMAZON_MANAGED_BUDGET]: {
    title: "algo-mode-selection.amazon_managed_budget",
    description: "algo-mode-selection.budget_allocation",
  },
};

export function toTargetConfig(algoModeConfig: Partial<AlgoModeConfig>, endOfCurrentMonth: string): TargetConfig {
  if (algoModeConfig.algoMode === AlgoMode.ACOS_TARGET) {
    return {
      algoMode: algoModeConfig.algoMode,
      acosTarget: algoModeConfig.acosTarget!,
      dailyBudget: algoModeConfig.dailyBudget!,
    };
  } else if (algoModeConfig.algoMode === AlgoMode.MONTHLY_BUDGET_TARGET) {
    return {
      algoMode: algoModeConfig.algoMode,
      monthlyBudget: algoModeConfig.monthlyBudget!,
      nextMonthlyBudget: algoModeConfig.nextMonthlyBudget ?? algoModeConfig.monthlyBudget!,
      endOfCurrentMonth,
    };
  } else if (algoModeConfig.algoMode === AlgoMode.PRODUCT_LAUNCH) {
    return {
      algoMode: algoModeConfig.algoMode,
      suggestedBid: algoModeConfig.suggestedBid!,
      dailyBudget: algoModeConfig.dailyBudget!,
    };
  } else if (algoModeConfig.algoMode === AlgoMode.AMAZON_MANAGED_BUDGET) {
    return {
      algoMode: algoModeConfig.algoMode,
      budgetFlight: algoModeConfig.budgetFlight,
    };
  }
  throw new Error("Invalid algo mode config");
}

@Component({
  selector: "app-algo-mode-selection",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslocoModule,
    IButtonDirective,
    ITooltipDirective,
    SpendProgressBarComponent,
    MonthlyBudgetWarningComponent,
    BudgetCurrencyBadgeComponent,
    IInputGroupDirective,
    IInputGroupInput,
    IInputGroupSuffixDirective,
    IFormFieldComponent,
    ILabelComponent,
    IErrorComponent,
    IIconComponent,
    IDatepickerComponent,
    IRadioComponent,
    IRadioGroupComponent,
  ],
  templateUrl: "./algo-mode-selection.component.html",
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlgoModeSelectionComponent implements OnInit, AfterViewInit {
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly currencyService = inject(CurrencyService);
  private readonly authService = inject(AuthService);
  private readonly datePipe = inject(DatePipe);
  private readonly translocoService = inject(TranslocoService);
  private readonly strategyService = inject(StrategyService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly AlgoModeStrKey: AlgoModeStrKey = AlgoModeStrKeys;

  acosTargetInput = viewChild<ElementRef>("acosTargetInput");
  monthlyBudgetInput = viewChild<ElementRef>("monthlyBudgetInput");
  suggestedBidInput = viewChild<ElementRef>("suggestedBidInput");
  tacosTargetInput = viewChild<ElementRef>("tacosTargetInput");
  budgetInput = viewChild<ElementRef>("budgetInput");
  readonly dateFormat = computed(() => DateUtils.getDateFormatString(this.locale() ?? "en-US"));
  readonly now = DateTime.utc();
  readonly nextYear = this.now.plus({ year: 1 });

  protected readonly AlgoMode = AlgoMode;
  @Input()
  set targetOnlyMode(value: boolean) {
    this._targetOnlyMode.set(value);
    if (this.form.controls.algoMode.value) {
      this.displayedAlgoModes.set([this.form.controls.algoMode.value!]);
    }
  }

  @Input()
  set selectedAlgoModeConfig(value: Partial<AlgoModeConfig> | undefined) {
    if (value) {
      this.form.patchValue(value);
      if (value.nextMonthlyBudget === undefined) {
        this.form.controls.nextMonthlyBudget.setValue(value.monthlyBudget);
      }

      if (this._targetOnlyMode()) {
        this.displayedAlgoModes.set([value.algoMode!]);
      }
      if (value.budgetFlight) {
        this.originalBudgetFlight.set(value.budgetFlight);
        this.form.controls.budget.setValue(value.budgetFlight.budget);
        this.form.controls.budgetDates.setValue({
          start: DateTime.fromISO(value.budgetFlight.startDate!),
          end: DateTime.fromISO(value.budgetFlight.endDate!),
        });
      }
    }
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled.set(value);
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  strategyId = input<number | undefined>(undefined);
  suggestedAcosTarget = input<number | undefined>(undefined);
  today = input<string>(DateTime.now().toFormat(API_DATE_FORMAT));
  currencyCode = input.required<Currency>();

  protected readonly _disabled = signal(false);
  protected readonly _targetOnlyMode = signal<boolean>(false);
  protected readonly am = toSignal(this.accountSelection.singleAccountMarketplaceSelection$);
  readonly originalBudgetFlight = signal<BudgetFlight | undefined>(undefined);
  public readonly flightStatus = computed<"future" | "active" | "ended">(() => {
    const flight = this.originalBudgetFlight();
    if (!flight?.startDate || !flight?.endDate) return "future";
    const today = DateTime.fromISO(this.today());
    const start = DateTime.fromISO(flight.startDate);
    const end = DateTime.fromISO(flight.endDate);
    if (start > today) return "future";
    if (end >= today) return "active";
    return "ended";
  });
  protected readonly displayedAlgoModes = signal<AlgoMode[]>([
    AlgoMode.ACOS_TARGET,
    AlgoMode.MONTHLY_BUDGET_TARGET,
    AlgoMode.PRODUCT_LAUNCH,
  ]);
  protected readonly currentMonthSpend = signal<number | undefined>(undefined);
  protected readonly dailyBudgetLowerBound = computed(
    () =>
      this.am() &&
      this.currencyCode() &&
      dailyBudgetLowerBound(this.currencyService.getCurrencyRate(this.currencyCode())),
  );
  protected readonly endOfCurrentMonth = computed(() => {
    if (!this.today()) return undefined;
    return DateTime.fromISO(this.today()).endOf("month").toJSDate();
  });
  protected readonly locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user.locale)));
  protected readonly minAllowedBid = computed(() => this.am() && this.am()!.minBid);
  protected readonly nextMonth = computed<string>(() => {
    if (!this.today()) {
      return "";
    }
    return this.translocoService.translate("switch-target-algo-modal.for_next_month", [
      this.datePipe.transform(
        DateTime.fromISO(this.today()).startOf("month").plus({ month: 1 }).toJSDate(),
        "MMMM y",
        undefined,
        this.translocoService.getActiveLang() != "dev" ? this.translocoService.getActiveLang() : "en",
      ),
    ]);
  });

  public readonly algoModeConfig = output<Partial<AlgoModeConfig>>();

  public readonly form = new FormGroup<AlgoModeForm>({
    algoMode: new FormControl<AlgoMode | undefined>(undefined, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    acosTarget: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    monthlyBudget: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    suggestedBid: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    dailyBudget: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    budget: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    budgetDates: new FormControl<IDateRange | undefined>(undefined, { nonNullable: true }),

    // for target change only
    nextMonthlyBudget: new FormControl<number | undefined>(undefined, { nonNullable: true }),
    tacosTarget: new FormControl<number | undefined>(undefined, { nonNullable: true }),
  });

  ngOnInit(): void {
    if (this._disabled()) {
      this.form.disable();
    }

    // TODO: this code is mixing reactive streams with imperative state updates, consider refactoring to use computed()
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am) => {
          return this.strategyService
            .getStrategyCurrentMonthSpend(am.accountId, am.marketplace, am.dspAdvertiserId, this.strategyId()!)
            .pipe(take(1));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((spend) => {
        this.currentMonthSpend.set(spend);
      });

    this.form.controls.suggestedBid.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.form.controls.dailyBudget.markAsTouched());

    this.form.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        map((value) => {
          const { budget, budgetDates, budgetFlight } = this.computeBudgetFlight(value);
          return {
            ...value,
            dailyBudget: value.dailyBudget ? Number(value.dailyBudget) : undefined,
            monthlyBudget: value.monthlyBudget ? Number(value.monthlyBudget) : undefined,
            nextMonthlyBudget: value.nextMonthlyBudget != null ? Number(value.nextMonthlyBudget) : undefined,
            acosTarget: value.acosTarget ? Number(value.acosTarget) : undefined,
            suggestedBid: value.suggestedBid ? Number(value.suggestedBid) : undefined,
            tacosTarget: value.tacosTarget ? Number(value.tacosTarget) : undefined,
            budget,
            budgetDates,
            budgetFlight,
          };
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.algoModeConfig.emit({
          ...value,
          isValid: this.form.valid,
        });
      });

    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((am) => {
        if (am) {
          this.updateAlgorithmValidator();
        }
      });
  }

  private computeBudgetFlight(
    value: Partial<{
      algoMode: AlgoMode | undefined;
      budget: number | undefined;
      budgetDates: IDateRange | undefined;
    }>,
  ) {
    const budget = value.budget ? Number(value.budget) : undefined;
    const budgetDates = value.budgetDates;
    const budgetFlight: BudgetFlight | undefined =
      value.algoMode === AlgoMode.AMAZON_MANAGED_BUDGET && budget && budgetDates?.start && budgetDates?.end
        ? {
            budget,
            startDate: budgetDates.start.toISODate() ?? undefined,
            endDate: budgetDates.end.toISODate() ?? undefined,
          }
        : undefined;
    return { budget, budgetDates, budgetFlight };
  }

  ngAfterViewInit(): void {
    this.focusInput(this.form.controls.algoMode.value!);
  }

  private updateAlgorithmValidator(): void {
    this.form.setValidators(
      algorithmValidator(
        this.currencyService.getCurrencyRate(this.currencyCode()),
        this.minAllowedBid()!,
        this._targetOnlyMode(),
      ),
    );
    this.form.updateValueAndValidity();
  }

  selectBudgetDates(dateRange: IDateRange | null): void {
    if (!dateRange) return;
    this.form.controls.budgetDates.setValue(dateRange);
  }

  selectEndDate(dateRange: IDateRange | null): void {
    if (!dateRange) return;
    const lockedStart = this.originalBudgetFlight()?.startDate
      ? DateTime.fromISO(this.originalBudgetFlight()!.startDate!)
      : (this.form.controls.budgetDates.value?.start ?? dateRange.start);
    this.form.controls.budgetDates.setValue({
      start: lockedStart,
      end: dateRange.start,
    });
  }

  protected changeAlgoMode(algoMode: AlgoMode | undefined) {
    if (!algoMode) {
      return;
    }

    this.form.controls.algoMode.setValue(algoMode);

    if (algoMode === AlgoMode.PRODUCT_LAUNCH) {
      this.form.controls.dailyBudget.setValue(this.dailyBudgetLowerBound()!);
      this.form.controls.suggestedBid.setValue(this.minAllowedBid()!);
    } else if (algoMode === AlgoMode.ACOS_TARGET) {
      this.form.controls.acosTarget.setValue(this.selectedAlgoModeConfig?.acosTarget ?? this.suggestedAcosTarget());
    }

    this.focusInput(algoMode);
  }

  private focusInput(mode: AlgoMode) {
    if (mode === AlgoMode.PRODUCT_LAUNCH) {
      this.suggestedBidInput()?.nativeElement.focus();
    } else if (mode === AlgoMode.ACOS_TARGET) {
      this.acosTargetInput()?.nativeElement.focus();
    } else if (mode === AlgoMode.MONTHLY_BUDGET_TARGET) {
      this.monthlyBudgetInput()?.nativeElement.focus();
    } else if (mode === AlgoMode.TACOS_TARGET) {
      this.tacosTargetInput()?.nativeElement.focus();
    } else if (mode === AlgoMode.AMAZON_MANAGED_BUDGET) {
      this.budgetInput()?.nativeElement.focus();
    }
  }

  protected deleteDailyBudget() {
    this.form.controls.dailyBudget.setValue(undefined);
  }
}

export function validateAlgoMode(path: SchemaPath<Partial<AlgoModeConfig> | null>, options?: { message?: string }) {
  validate(path, ({ value }) => {
    return value()?.isValid ? null : { kind: "invalidAlgo", message: options?.message || "Invalid algorithm" };
  });
}
